import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ModakBackdrop from "./ModakBackdrop";
import { useRoundGate } from "./useRoundGate";
import { useElapsedTimer } from "./useElapsedTimer";
import { submitModakRound } from "./modakApi";
import { RoundHeader } from "./RoundChrome";
import useIsNarrow from "./useIsNarrow";

const QUESTIONS = [
  { img: "/modak-assets/R1/1.png", accepted: ["puran poli"] },
  { img: "/modak-assets/R1/2.png", accepted: ["satori"] },
  { img: "/modak-assets/R1/3.png", accepted: ["shreekhand", "shrikhand"] },
  { img: "/modak-assets/R1/4.png", accepted: ["coconut rice"] },
];

const RULES = [
  "Insert the name of the Food in the text box",
  "Right answer will turn the box green wrong answer will turn it red",
];

export default function Round1() {
  const navigate = useNavigate();
  const { loading, user, adminToken, timerKey, status } = useRoundGate(1);
  const time = useElapsedTimer(timerKey);
  const narrow = useIsNarrow();

  const [answers, setAnswers] = useState(["", "", "", ""]);
  const [locked, setLocked] = useState([false, false, false, false]);
  const [score, setScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [mobileIndex, setMobileIndex] = useState(0);
  const basePointsRef = useRef(0);
  const submittedRef = useRef(false);

  React.useEffect(() => {
    if (status) {
      basePointsRef.current = status.points ?? 0;
      setScore(basePointsRef.current);
    }
  }, [status]);

  if (loading) return null;

  const isCorrect = (idx, value) =>
    QUESTIONS[idx].accepted.includes(value.trim().toLowerCase());

  const handleChange = (idx, value) => {
    if (locked[idx]) return;
    const next = [...answers];
    next[idx] = value;
    setAnswers(next);

    if (isCorrect(idx, value)) {
      const nextLocked = [...locked];
      nextLocked[idx] = true;
      setLocked(nextLocked);
      setScore((s) => s + 1);
    }
  };

  const allFilled = answers.every((a) => a.trim().length > 0);
  const visibleIndexes = narrow ? [mobileIndex] : QUESTIONS.map((_, i) => i);

  const finish = async (finalPoints) => {
    // Ref guard (not just the `submitting` state check in the callers below)
    // so a fast double-click on Submit/SKIP can't race two finish() calls
    // through before the re-render lands — that raced pair would both read
    // the same not-yet-linked reportId and each POST a brand-new external
    // report instead of the second one PUTting an update.
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    await submitModakRound(adminToken, {
      userId: user.userId,
      round: 1,
      points: finalPoints,
      time,
      answers: answers.map((a) => a.trim().toLowerCase()).join(","),
      user,
    });
    navigate("/home");
  };

  const handleSubmit = () => {
    if (!allFilled || submitting) return;
    finish(score);
  };

  const handleSkip = () => {
    if (submitting) return;
    finish(basePointsRef.current);
  };

  return (
    <ModakBackdrop>
      <div className="modak-round">
        <RoundHeader time={time} score={score} rules={RULES} onSkip={handleSkip} />

        <div className="modak-round-heading-row">
          <span className="modak-round-label">Bhog-a-licious</span>
          <div className="modak-round-instruction">
            Name these bhogs (devotional offerings) specially made for
            <br />
            Ganesh Chaturthi
          </div>
        </div>

        <div className={narrow ? "modak-slider-track" : undefined}>
          {narrow && (
            <button
              type="button"
              className="modak-slider-side-arrow modak-slider-side-arrow--prev"
              onClick={() => setMobileIndex((i) => Math.max(0, i - 1))}
              disabled={mobileIndex === 0}
              aria-label="Previous question"
            >
              ‹
            </button>
          )}

          <div className="modak-r1-grid">
            {visibleIndexes.map((idx) => {
              const q = QUESTIONS[idx];
              return (
                <div className="modak-r1-card" key={idx}>
                  <img src={q.img} alt={`Bhog ${idx + 1}`} />
                  <input
                    type="text"
                    placeholder="Answer"
                    value={answers[idx]}
                    readOnly={locked[idx]}
                    onChange={(e) => handleChange(idx, e.target.value)}
                    style={{
                      background: locked[idx]
                        ? "#c6ffc6"
                        : answers[idx] && !isCorrect(idx, answers[idx])
                        ? "#ffdada"
                        : "#fff",
                    }}
                  />
                </div>
              );
            })}
          </div>

          {narrow && (
            <button
              type="button"
              className="modak-slider-side-arrow modak-slider-side-arrow--next"
              onClick={() => setMobileIndex((i) => Math.min(QUESTIONS.length - 1, i + 1))}
              disabled={mobileIndex === QUESTIONS.length - 1}
              aria-label="Next question"
            >
              ›
            </button>
          )}
        </div>

        {narrow && (
          <div className="modak-slider-nav">
            <span className="modak-slider-counter">
              {mobileIndex + 1} / {QUESTIONS.length}
            </span>
          </div>
        )}

        <button type="button" className="modak-btn modak-submit-btn" disabled={!allFilled || submitting} onClick={handleSubmit}>
          Submit
        </button>
      </div>
    </ModakBackdrop>
  );
}
